import type { Params } from 'react-router'

import type { SummaryIdeaStatus } from '@/db/ideas/schema'
import {
  PortfolioWorkspaceSegment,
  type ProductWorkspaceParams,
  ProductWorkspaceSegment,
} from '@/lib/workspace/routerTypes'
import type { OrganizationEntryScope } from '@/types/organization'

export const organizationRoute = (organizationId?: string) =>
  organizationId ? `/organizations/${organizationId}` : '/organizations'

export const portfolioRoute = (organizationId: string, portfolioId: string) =>
  `${organizationRoute(organizationId)}/portfolios/${portfolioId}`

export const portfolioWorkspaceRoute = (
  organizationId: string,
  portfolioId: string,
  segment: PortfolioWorkspaceSegment
) => {
  return `${portfolioRoute(organizationId, portfolioId)}/${segment}`
}

export const portfolioHomeRoute = (
  organizationId: string,
  portfolioId: string
) => {
  return portfolioWorkspaceRoute(
    organizationId,
    portfolioId,
    PortfolioWorkspaceSegment.Home
  )
}

const productBaseRoute = (
  organizationId: string,
  portfolioId: string,
  productId: string
) => `${portfolioRoute(organizationId, portfolioId)}/products/${productId}`

export const productWorkspaceRoute = (
  { organizationId, portfolioId, productId }: ProductWorkspaceParams,
  segment: ProductWorkspaceSegment
) => `${productBaseRoute(organizationId, portfolioId, productId)}/${segment}`

export const addProductRoute = (
  organizationId: string,
  portfolioId: string
) => {
  return `${portfolioRoute(organizationId, portfolioId)}/products/new`
}

export const productSettingsRoute = ({
  organizationId,
  portfolioId,
  productId,
}: ProductWorkspaceParams) => {
  return `${productBaseRoute(organizationId, portfolioId, productId)}/settings`
}

export const productSettingsTabRoute = (
  params: ProductWorkspaceParams,
  tab: 'context' | 'integrations'
) => {
  const base = productSettingsRoute(params)
  return `${base}/${tab}`
}

export const organizationEntryPath = (
  organizationId: string,
  entryScope: OrganizationEntryScope
) => {
  return productWorkspaceRoute(
    {
      organizationId,
      portfolioId: entryScope.portfolioId,
      productId: entryScope.productId,
    },
    ProductWorkspaceSegment.Home
  )
}

interface HypothesisBaseRouteProps {
  organizationId: string
  portfolioId: string
  productId: string
}

export const hypothesisBaseRoute = ({
  organizationId,
  portfolioId,
  productId,
}: HypothesisBaseRouteProps) => {
  return productWorkspaceRoute(
    { organizationId, portfolioId, productId },
    ProductWorkspaceSegment.Hypotheses
  )
}

interface HypothesisRouteProps extends HypothesisBaseRouteProps {
  hypothesisId?: string
}

export const hypothesisRoute = ({
  hypothesisId,
  ...baseRouteProps
}: HypothesisRouteProps) => {
  const baseRoute = hypothesisBaseRoute(baseRouteProps)

  return `${baseRoute}/${hypothesisId}`
}

interface ExperimentBaseRouteProps {
  organizationId: string
  portfolioId: string
  productId: string
}

export const experimentBaseRoute = ({
  organizationId,
  portfolioId,
  productId,
}: ExperimentBaseRouteProps) => {
  return productWorkspaceRoute(
    { organizationId, portfolioId, productId },
    ProductWorkspaceSegment.Experiments
  )
}

interface ExperimentRouteProps extends ExperimentBaseRouteProps {
  experimentId: string
}

export const experimentRoute = ({
  experimentId,
  ...baseRouteProps
}: ExperimentRouteProps) => {
  const baseRoute = experimentBaseRoute(baseRouteProps)

  return `${baseRoute}/${experimentId}`
}

interface ArtifactBaseRouteProps {
  organizationId: string
  portfolioId: string
  productId?: string
}

export const artifactBaseRoute = ({
  organizationId,
  portfolioId,
  productId,
}: ArtifactBaseRouteProps) => {
  if (productId) {
    return productWorkspaceRoute(
      { organizationId, portfolioId, productId },
      ProductWorkspaceSegment.Artifacts
    )
  }

  return portfolioWorkspaceRoute(
    organizationId,
    portfolioId,
    PortfolioWorkspaceSegment.Artifacts
  )
}

interface ArtifactRouteProps extends ArtifactBaseRouteProps {
  artifactId: string
}

export const artifactRoute = ({
  artifactId,
  ...baseRouteProps
}: ArtifactRouteProps) => {
  const baseRoute = artifactBaseRoute(baseRouteProps)
  return `${baseRoute}/${artifactId}`
}

interface InsightBaseRouteProps {
  organizationId: string
  portfolioId: string
  productId: string
}

export const insightBaseRoute = ({
  organizationId,
  portfolioId,
  productId,
}: InsightBaseRouteProps) => {
  return productWorkspaceRoute(
    { organizationId, portfolioId, productId },
    ProductWorkspaceSegment.Insights
  )
}

interface InsightRouteProps extends InsightBaseRouteProps {
  insightId: string
}

export const insightRoute = ({
  insightId,
  ...baseRouteProps
}: InsightRouteProps) => {
  const baseRoute = insightBaseRoute(baseRouteProps)
  return `${baseRoute}/${insightId}`
}

export interface IdeaBaseRouteProps {
  organizationId: string
  portfolioId: string
  productId: string
}

export const PORTFOLIO_ROADMAP_RETURN_PARAM = 'from'
export const PORTFOLIO_ROADMAP_RETURN_VALUE = 'portfolio-roadmap'

interface IdeaBaseRouteOptions {
  status?: SummaryIdeaStatus
  from?: typeof PORTFOLIO_ROADMAP_RETURN_VALUE
}

export const ideaBaseRoute = (
  { organizationId, portfolioId, productId }: IdeaBaseRouteProps,
  { status, from }: IdeaBaseRouteOptions = {}
) => {
  const ideasRoute = productWorkspaceRoute(
    { organizationId, portfolioId, productId },
    ProductWorkspaceSegment.Ideas
  )
  const route = status ? `${ideasRoute}/${status}` : ideasRoute

  if (!from) {
    return route
  }

  const searchParams = new URLSearchParams({
    [PORTFOLIO_ROADMAP_RETURN_PARAM]: from,
  })

  return `${route}?${searchParams.toString()}`
}

interface IdeaRouteProps extends IdeaBaseRouteProps {
  ideaId: string
}

export const ideaRoute = ({ ideaId, ...baseRouteProps }: IdeaRouteProps) => {
  const baseRoute = ideaBaseRoute(baseRouteProps)
  return `${baseRoute}/${ideaId}`
}

export const userWorkspaceEntryPath = (
  organizationId: string,
  entryScope: OrganizationEntryScope
) => organizationEntryPath(organizationId, entryScope)

export const isOrganizationScopedPath = (pathname: string) =>
  pathname === '/organizations' || pathname.startsWith('/organizations/')

export const isOrganizationAdminPath = (pathname: string) =>
  pathname === '/organizations' || /^\/organizations\/[^/]+$/.test(pathname)

export const organizationIdFromPath = (pathname: string) =>
  /^\/organizations\/([^/]+)/.exec(pathname)?.[1] ?? null

export const isPortfolioWorkspaceHomePath = (pathname: string) =>
  /\/organizations\/[^/]+\/portfolios\/[^/]+\/home$/.test(pathname)

export const isProductWorkspaceHomePath = (pathname: string) =>
  /\/organizations\/[^/]+\/portfolios\/[^/]+\/products\/[^/]+\/home$/.test(
    pathname
  )

export type WorkspacePathType =
  | 'portfolio-home'
  | 'portfolio-workspace'
  | 'product-workspace'
  | 'other'

export const getWorkspacePathType = (pathname: string): WorkspacePathType => {
  if (/^\/organizations\/[^/]+\/portfolios\/[^/]+\/home$/.test(pathname)) {
    return 'portfolio-home'
  }

  if (
    /^\/organizations\/[^/]+\/portfolios\/[^/]+\/products\/[^/]+/.test(pathname)
  ) {
    return 'product-workspace'
  }

  if (/^\/organizations\/[^/]+\/portfolios\/[^/]+/.test(pathname)) {
    return 'portfolio-workspace'
  }

  return 'other'
}

export const isOrganizationHomePath = (pathname: string) =>
  isPortfolioWorkspaceHomePath(pathname) || isProductWorkspaceHomePath(pathname)

export const routePatternFromPath = (
  pathname: string,
  params: Readonly<Params>
) => {
  const paramNamesByValue = new Map(
    Object.entries(params).map(([paramName, value]) => [value, paramName])
  )

  return pathname
    .split('/')
    .map((segment) => {
      const paramName = paramNamesByValue.get(segment)

      return paramName ? `:${paramName}` : segment
    })
    .join('/')
}
